import { createRootRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import { COMPANIES, type Company } from '@dcc-erp/shared';

import { AppShell } from '../components/layout';
import { FullPageLoader, RequireAuth, useAuth } from '../lib/auth';
import { useActiveCompany, useApplyPendingLoginCompany } from '../lib/queries/companies';

export const Route = createRootRoute({
  component: RootLayout,
});

const PUBLIC_PATHS = new Set<string>(['/login', '/auth/callback', '/']);

function RootLayout() {
  const location = useLocation();
  const pathname = location.pathname;
  const isPublic = PUBLIC_PATHS.has(pathname);

  if (isPublic) {
    return (
      <div className="min-h-screen bg-[color:var(--color-bg)] text-[color:var(--color-text)]">
        <Outlet />
      </div>
    );
  }

  return (
    <RequireAuth>
      <AuthedShell />
    </RequireAuth>
  );
}

function AuthedShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useAuth();
  // Honour the company picked on the login screen once auth + companies resolve.
  // While unresolved, `resolved` gates the render below so the dashboard never
  // mounts (and never fires queries) against the default company first.
  const { resolved } = useApplyPendingLoginCompany();
  const activeCompany = useActiveCompany();

  // Mirror the active company onto `<html data-company="...">` so prototype CSS
  // (gradients, badges) stays in sync with the selected company. Skip while the
  // login-company gate is open — useApplyPendingLoginCompany owns the theme then
  // (painting the chosen company), so this can't briefly flip it to the default.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    if (!resolved) return;
    if (activeCompany?.code) {
      document.documentElement.setAttribute('data-company', activeCompany.code);
    }
  }, [activeCompany?.code, resolved]);

  if (!resolved) return <FullPageLoader />;

  const company: Company = activeCompany ?? COMPANIES[0]!;
  const active = location.pathname.replace(/^\//, '') || 'dashboard';

  return (
    <AppShell
      company={company}
      active={active}
      onSelect={(id) => {
        // Phase-1: most sidebar IDs don't have routes yet — TanStack will land on NotFound.
        void navigate({ to: '/' + id });
      }}
      onLogout={() => {
        void logout();
      }}
    >
      <Outlet />
    </AppShell>
  );
}
